import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Switch,
  Modal,
  Form,
  Input,
  Select,
  Checkbox,
  Flex,
  notification,
} from "antd";
import {
  EditOutlined,
  MinusCircleFilled,
  PlusOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { Home2 } from "iconsax-react";
import Layout from "../../components/layouts/Layout";
import BreadcrumbBlock from "../../components/BreadcrumbBlock/BreadcrumbBlock";
import TelegramSetupGuideModal from "./components/TelegramSetupGuideModal";
import "./BotIntegrations.scss";
import { mixPostV1 } from "../../services/mixin";
import {
  BotIntegrationItem,
  BotReportCategoryDetail,
} from "../../components/Interface/InterfaceTemp";

interface BotItem {
  id: string;
  name: string;
  platform: string;
  token: string;
  defaultChatId: string;
  selectedReports: string[]; // "machine" | "staff" | "manager"
  active: boolean;
}

const LOCAL_STORAGE_KEY = "cmd_bot_integrations";

const BotIntegrations: React.FC = () => {
  const navigate = useNavigate();
  const service_id = localStorage.getItem("service_id");

  const [bots, setBots] = useState<BotItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);
  const [editingBot, setEditingBot] = useState<BotItem | null>(null);
  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();

  const openNotification = (
    type: "success" | "error" = "success",
    message: string = "Updated successfully!",
  ) => {
    api[type]({
      message,
      placement: "bottomRight",
      className: "single-message",
    });
  };

  const fetchBots = async () => {
    setLoading(true);
    try {
      const res = await mixPostV1(
        "/api/webAdmin/gListBotIntergretions.php",
        {},
      );
      if (res && res.status === 1 && Array.isArray(res.data)) {
        const mapped: BotItem[] = res.data.map((item: BotIntegrationItem) => {
          let selectedReports: string[] = [];
          if (item.report_details) {
            let detailsStr = item.report_details;
            if (typeof detailsStr === "string") {
              if (detailsStr.startsWith("json, ")) {
                detailsStr = detailsStr.substring(6);
              }
              try {
                const detailsList = JSON.parse(detailsStr);
                if (Array.isArray(detailsList)) {
                  selectedReports = detailsList
                    .filter((d: any) => d && d.report_name)
                    .map((d: any) => String(d.report_name).toLowerCase())
                    .filter(Boolean);
                }
              } catch (e) {
                console.error("Failed to parse report_details:", e);
              }
            } else if (Array.isArray(detailsStr)) {
              selectedReports = (detailsStr as any[])
                .filter((d: any) => d && d.report_name)
                .map((d: any) => String(d.report_name).toLowerCase())
                .filter(Boolean);
            }
          }
          return {
            id: String(item.bi_id),
            name: item.bot_name,
            platform:
              String(item.flatform) === "1"
                ? "telegram"
                : String(item.flatform || "telegram"),
            token: item.bot_token,
            defaultChatId: item.chat_id,
            selectedReports,
            active:
              Number(item.is_active) === 1 || String(item.is_active) === "true",
          };
        });
        setBots(mapped);
      } else {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (stored) {
          setBots(JSON.parse(stored));
        }
      }
    } catch (error) {
      console.error("Error loading bots:", error);
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored) {
        setBots(JSON.parse(stored));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Number(service_id) !== 3) {
      window.dispatchEvent(new CustomEvent("openUpgradeModal"));
      navigate("/");
    } else {
      fetchBots();
    }
  }, []);

  const saveBots = (updatedBots: BotItem[]) => {
    setBots(updatedBots);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedBots));
  };

  // Open modal for add
  const handleOpenAdd = () => {
    setEditingBot(null);
    form.resetFields();
    form.setFieldsValue({
      platform: "telegram",
      selectedReports: [],
      active: true,
    });
    setIsModalOpen(true);
  };

  // Open modal for edit
  const handleOpenEdit = (bot: BotItem) => {
    setEditingBot(bot);
    form.setFieldsValue({
      name: bot.name,
      platform: bot.platform,
      token: bot.token,
      defaultChatId: bot.defaultChatId,
      selectedReports: bot.selectedReports,
      active: Boolean(bot.active),
    });
    setIsModalOpen(true);
  };

  // Handle delete
  const handleDelete = (id: string, name: string) => {
    Modal.confirm({
      title: "Delete Bot Integration",
      content: `Are you sure you want to delete "${name}"?`,
      okText: "Yes, delete",
      cancelText: "No",
      okType: "danger",
      centered: true,
      className: "rs-confirm-modal modal-delete V2",
      onOk: async () => {
        setLoading(true);
        try {
          const res = await mixPostV1(
            "/api/webAdmin/deleteBotIntergretions.php",
            { bi_id: id },
          );
          if (res && res.status === 1) {
            openNotification("success", "Bot integration deleted!");
            fetchBots();
          } else {
            openNotification(
              "error",
              res.message || "Failed to delete bot integration",
            );
          }
        } catch (e) {
          console.error("Error deleting bot:", e);
          openNotification("error", "Error connecting to server");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Handle active status toggle
  const handleToggleActive = async (id: string, checked: boolean) => {
    setLoading(true);
    try {
      const res = await mixPostV1(
        "/api/webAdmin/updateActiveBotIntergretions.php",
        {
          bi_id: id,
          is_active: checked ? 1 : 0,
        },
      );
      if (res && res.status === 1) {
        openNotification("success", `Bot active status updated!`);
        fetchBots();
      } else {
        openNotification(
          "error",
          res.message || "Failed to update bot active status",
        );
      }
    } catch (e) {
      console.error("Error toggling bot status:", e);
      openNotification("error", "Error connecting to server");
    } finally {
      setLoading(false);
    }
  };

  // Form submission
  const handleFormFinish = async (values: any) => {
    if (editingBot) {
      // Edit mode
      const params: any = {
        bi_id: editingBot.id,
        bot_name: values.name,
        flatform: 1, // 1: telegram
        bot_token: values.token,
        chat_id: values.defaultChatId,
        is_active: values.active ? 1 : 0,
      };

      const selectedIds = (values.selectedReports || [])
        .map((r: string) => {
          if (r === "machine") return 1;
          if (r === "staff") return 2;
          if (r === "manager") return 3;
          return 0;
        })
        .filter(Boolean);

      selectedIds.forEach((id: number, idx: number) => {
        params[`report_category_ids[${idx}]`] = id;
      });

      setLoading(true);
      try {
        const res = await mixPostV1(
          "/api/webAdmin/editBotIntergretions.php",
          params,
        );
        if (res && res.status === 1) {
          openNotification("success", "Bot integration updated!");
          fetchBots();
        } else {
          openNotification(
            "error",
            res.message || "Failed to update bot integration",
          );
        }
      } catch (e) {
        console.error("Error updating bot:", e);
        openNotification("error", "Error connecting to server");
      } finally {
        setLoading(false);
      }
    } else {
      const params: any = {
        bot_name: values.name,
        flatform: 1,
        bot_token: values.token,
        chat_id: values.defaultChatId,
        is_active: values.active ? 1 : 0,
      };

      const selectedIds = (values.selectedReports || [])
        .map((r: string) => {
          if (r === "machine") return 1;
          if (r === "staff") return 2;
          if (r === "manager") return 3;
          return 0;
        })
        .filter(Boolean);

      selectedIds.forEach((id: number, idx: number) => {
        params[`report_category_ids[${idx}]`] = id;
      });

      setLoading(true);
      try {
        const res = await mixPostV1(
          "/api/webAdmin/addBotIntergretions.php",
          params,
        );
        if (res && res.status === 1) {
          openNotification("success", "New bot integration added!");
          fetchBots();
        } else {
          openNotification(
            "error",
            res.message || "Failed to add bot integration",
          );
        }
      } catch (e) {
        console.error("Error adding bot:", e);
        openNotification("error", "Error connecting to server");
      } finally {
        setLoading(false);
      }
    }
    setIsModalOpen(false);
  };

  // Format Report Category column
  const formatReportCategory = (reports: string[]) => {
    if (!reports || reports.length === 0) return "None";
    if (reports.length === 3) return "All Report";

    // Format individual selected reports nicely
    const list = reports.map((r) => {
      if (r === "machine") return "Machine";
      if (r === "staff") return "Staff";
      if (r === "manager") return "Manager";
      return r;
    });

    // In the design, if Manager and Staff are selected, it displays "Manager / Staff"
    // Let's sort to match the order: Machine, Staff, Manager if multiple
    const order = ["Machine", "Staff", "Manager"];
    list.sort((a, b) => order.indexOf(a) - order.indexOf(b));

    return list.join(" / ");
  };

  // Column definitions
  const columns = [
    {
      title: "No",
      key: "stt",
      width: 60,
      align: "center" as const,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Bot Name",
      dataIndex: "name",
      key: "name",
      render: (text: any) => (
        <span className="bot-table-name">{String(text || "")}</span>
      ),
    },
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      render: (text: any) => {
        const str = String(text || "telegram");
        return (
          <span className="bot-table-platform">
            {str.charAt(0).toUpperCase() + str.slice(1)}
          </span>
        );
      },
    },
    {
      title: "Bot Token / API Key",
      dataIndex: "token",
      key: "token",
      render: (text: any) => {
        const str = String(text || "");
        return (
          <span className="bot-table-token" title={str}>
            {str.length > 25
              ? `${str.substring(0, 15)}...${str.substring(str.length - 10)}`
              : str}
          </span>
        );
      },
    },
    {
      title: "Default Chat ID/ Channel",
      dataIndex: "defaultChatId",
      key: "defaultChatId",
      render: (text: any) => (
        <span className="bot-table-chatid">{String(text || "")}</span>
      ),
    },
    {
      title: "Report Category",
      dataIndex: "selectedReports",
      key: "reportCategory",
      render: (reports: string[]) => (
        <span className="bot-table-category">
          {formatReportCategory(reports)}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      align: "center" as const,
      render: (_: any, record: BotItem) => (
        <Flex align="center" justify="center" gap={12}>
          <Switch
            checked={record.active}
            size="small"
            onChange={(checked) => handleToggleActive(record.id, checked)}
            className="bot-status-switch"
          />
          <EditOutlined
            className="bot-action-btn bot-edit-btn"
            onClick={() => handleOpenEdit(record)}
          />
          <MinusCircleFilled
            className="bot-action-btn bot-delete-btn"
            onClick={() => handleDelete(record.id, record.name)}
          />
        </Flex>
      ),
    },
  ];

  return (
    <>
      {contextHolder}

      <Layout contentClassName="bot-integrations-layout">
        <BreadcrumbBlock
          items={[
            {
              title: (
                <Flex align="center" gap={16}>
                  <Home2 variant="Bulk" />
                  <Link to="/">CMD Tech</Link>
                </Flex>
              ),
            },
            { title: "Bot Intergrations" },
          ]}
          title="Bot Intergrations"
          description=""
        />

        <div className="bot-integrations-container">
          <Flex
            justify="space-between"
            align="center"
            className="bot-header-section"
          >
            <h2 className="bot-page-title">Bot Intergrations</h2>
            <Flex gap={12} align="center">
              <Button
                className="rs-button bot-guide-btn"
                icon={<QuestionCircleOutlined />}
                onClick={() => setIsGuideModalOpen(true)}
              >
                Setup Guide
              </Button>
              <Button
                type="primary"
                className="rs-button bot-add-btn"
                icon={<PlusOutlined />}
                onClick={handleOpenAdd}
              >
                Add new bot
              </Button>
            </Flex>
          </Flex>

          <Table
            columns={columns}
            dataSource={bots}
            rowKey="id"
            className="rs-table table-text-nowrap bot-integrations-table"
            pagination={false}
            loading={loading}
            scroll={{ x: true }}
            locale={{ emptyText: "No bot integrations setup yet." }}
          />
        </div>
      </Layout>

      {/* Add/Edit Modal */}
      <Modal
        title={null}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        centered
        className="rs-modal bot-form-modal"
        width={480}
      >
        <div className="bot-modal-content">
          <h3 className="bot-modal-title">
            {editingBot ? "Edit Bot Integration" : "Add New Bot"}
          </h3>

          <div
            className="bot-modal-guide-banner"
            onClick={() => setIsGuideModalOpen(true)}
          >
            <QuestionCircleOutlined className="guide-icon" />
            <span>
              Need help getting Token or Chat ID?{" "}
              <strong>View setup guide</strong>
            </span>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleFormFinish}
            className="rs-form bot-form"
            requiredMark={false}
          >
            <Form.Item
              label="Bot name"
              name="name"
              rules={[{ required: true, message: "Please enter a bot name" }]}
            >
              <Input placeholder="Enter bot name" className="rs-input" />
            </Form.Item>

            <Form.Item label="Platform" name="platform">
              <div className="platform-select-container">
                <Select
                  disabled
                  defaultValue="telegram"
                  className="rs-select"
                  options={[{ value: "telegram", label: "Telegram" }]}
                />
              </div>
            </Form.Item>

            <Form.Item
              label="Bot Token / API Key"
              name="token"
              rules={[
                {
                  required: true,
                  message: "Please enter the bot token / API Key",
                },
              ]}
            >
              <Input placeholder="Enter bot token" className="rs-input" />
            </Form.Item>

            <Form.Item
              label="Default Chat ID/ Channel"
              name="defaultChatId"
              rules={[
                {
                  required: true,
                  message: "Please enter default chat ID or channel name",
                },
              ]}
            >
              <Input
                placeholder="Enter chat ID or channel"
                className="rs-input"
                type="number"
              />
            </Form.Item>

            <Form.Item
              label="Selected Reports"
              name="selectedReports"
              rules={[
                {
                  validator: (_, value) =>
                    value && value.length > 0
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error("Please select at least one report type"),
                        ),
                },
              ]}
            >
              <Checkbox.Group className="bot-checkbox-group">
                <Checkbox value="machine" className="rs-checkbox">
                  Machine
                </Checkbox>
                <Checkbox value="staff" className="rs-checkbox">
                  Staff
                </Checkbox>
                <Checkbox value="manager" className="rs-checkbox">
                  Manager
                </Checkbox>
              </Checkbox.Group>
            </Form.Item>

            <Form.Item label={null}>
              <Flex
                align="center"
                gap={8}
                className="bot-active-toggle-container"
              >
                <Form.Item name="active" valuePropName="checked" noStyle>
                  <Switch size="small" className="bot-status-switch" />
                </Form.Item>
                <span className="bot-active-label">Active</span>
              </Flex>
            </Form.Item>

            <Flex justify="center" gap={16} className="bot-modal-actions">
              <Button
                className="rs-button bot-modal-cancel"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                className="rs-button bot-modal-submit"
              >
                {editingBot ? "Save changes" : "Add new bot"}
              </Button>
            </Flex>
          </Form>
        </div>
      </Modal>

      <TelegramSetupGuideModal
        open={isGuideModalOpen}
        onClose={() => setIsGuideModalOpen(false)}
        onOpenAddBot={() => {
          setIsGuideModalOpen(false);
          handleOpenAdd();
        }}
      />
    </>
  );
};

export default BotIntegrations;
